import React from 'react';
import { Link } from '@tanstack/react-router';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  Dialog,
  DialogContent,
  DialogTrigger,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Page,
  PageHeader,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { Ticket } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useTicketingProviders, type TicketingProviderSummary } from '../../hooks/useTicketingProviders';
import { getTicketingProviderLabel } from '../../utils/ticketingProviders';
import { safeHref } from '../../utils/safeHref';
import { getExploratoryTestStatusMeta } from '../../features/exploratory-tests/types';
import {
  FilingSettingsSection,
  TicketConfigPanel,
  TicketSourceSelect,
  useDefaultTicketSource,
  useTicketEnvironments,
  useTicketProperties,
  useTicketSettings,
  useTickets,
  isSelectableTicketEnvironment,
  ticketEnvironmentLabel,
  type TicketListItem,
  type TicketSettings,
} from '../../features/tickets';

function TicketKeyCell({ ticket }: { ticket: TicketListItem }) {
  const href = safeHref(ticket.url);
  if (!href) return <Text size="sm">{ticket.identifier}</Text>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:underline"
      title={`Open ${ticket.identifier} in Jira`}
    >
      <Text size="sm" weight="medium">
        {ticket.identifier}
      </Text>
    </a>
  );
}

function RunCell({ ticket }: { ticket: TicketListItem }) {
  if (!ticket.run) {
    return (
      <Text size="sm" className="text-muted-foreground">
        Not run yet
      </Text>
    );
  }
  const meta = getExploratoryTestStatusMeta(ticket.run.status);
  return (
    <Link
      to="/exploratory/$exploratoryTestId"
      params={{ exploratoryTestId: ticket.run.id }}
      className="hover:underline"
    >
      <TagBadge name={meta.label} color={meta.color} hideDot />
    </Link>
  );
}

const columns: ColumnDef<StockFeatures, TicketListItem>[] = [
  {
    id: 'ticket',
    header: 'Ticket',
    cell: ({ row }) => <TicketKeyCell ticket={row.original} />,
    meta: { flex: 0.8 },
  },
  {
    id: 'title',
    header: 'Title',
    cell: ({ row }) => (
      <Text size="sm" className="truncate">
        {row.original.title}
      </Text>
    ),
    meta: { flex: 3 },
  },
  {
    id: 'status',
    header: 'Ticket status',
    cell: ({ row }) => <TagBadge name={row.original.status} color="gray" hideDot />,
    meta: { flex: 1 },
  },
  {
    id: 'run',
    header: 'Latest run',
    cell: ({ row }) => <RunCell ticket={row.original} />,
    meta: { flex: 1 },
  },
  {
    id: 'finished',
    header: 'Finished',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {row.original.run?.finishedAt ? new Date(row.original.run.finishedAt).toLocaleString() : '—'}
      </Text>
    ),
    meta: { flex: 1.2 },
  },
];

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex gap="md">
      <Text size="sm" className="text-muted-foreground w-40 shrink-0">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Flex>
  );
}

/** Read-only view of the saved configuration, with a button that opens the form in a dialog. */
function TicketConfigSummary({ settings }: { settings: TicketSettings }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const properties = useTicketProperties().data?.items ?? [];
  const environments = useTicketEnvironments(settings.tagTriggerPropertyId).data?.items ?? [];
  const application = properties.find((property) => property.id === settings.tagTriggerPropertyId);
  const environment = environments.find((item) => item.id === settings.tagTriggerEnvironmentId);

  return (
    <Card padding="lg">
      <CardHeader>
        <Flex align="center" justify="between" gap="md">
          <div>
            <Heading level={3}>Configuration</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Tag a Jira ticket and an agent tests it, then writes the results back to the ticket.
            </Text>
          </div>
          <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            <Button variant="outline" size="sm">
              Configure
            </Button>
            <Dialog title="Ticket testing configuration">
              <DialogContent>
                <TicketConfigPanel settings={settings} onSaved={() => setIsOpen(false)} />
              </DialogContent>
            </Dialog>
          </DialogTrigger>
        </Flex>
      </CardHeader>
      <CardContent>
        <Flex direction="column" gap="sm">
          <SummaryRow label="Platform" value="Jira" />
          <SummaryRow label="Tag" value={settings.tagTriggerLabel ?? ''} />
          <SummaryRow label="Application" value={application?.name ?? 'Not set'} />
          <SummaryRow label="Environment" value={
              !environment
                ? 'Application default'
                : isSelectableTicketEnvironment(environment)
                  ? ticketEnvironmentLabel(environment)
                  : 'Unavailable: choose another environment'
            } />
          <SummaryRow
            label="Additional instructions"
            value={settings.tagTriggerInstructions ? 'Set' : 'None'}
          />
        </Flex>
      </CardContent>
    </Card>
  );
}

/** A card for a part of the page the default source's provider does not offer, saying so plainly. */
function UnsupportedCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card padding="lg">
      <CardHeader>
        <Heading level={3}>{title}</Heading>
      </CardHeader>
      <CardContent>
        <Text size="sm" className="text-muted-foreground">
          {children}
        </Text>
      </CardContent>
    </Card>
  );
}

/** The Filing card: GitHub's defaults form, or a plain note when the provider has no filing defaults. */
function FilingCard({ provider }: { provider: TicketingProviderSummary }) {
  if (provider.capabilities.filingSettings) return <FilingSettingsSection />;
  return (
    <UnsupportedCard title="Filing">
      Filing defaults are not available for {provider.displayName} yet.
    </UnsupportedCard>
  );
}

/** Jira as the default source: the tag-trigger configuration, then the tagged tickets with their runs. */
function JiraTicketTesting({ provider }: { provider: TicketingProviderSummary }) {
  const settingsQuery = useTicketSettings();
  const ticketsQuery = useTickets();

  // Settings are the source of truth for the tag; the tickets response can lag a save by a moment.
  const savedTag = settingsQuery.data?.data.tagTriggerLabel || null;
  const ticketsLabel = ticketsQuery.data?.data.label ?? null;
  const tickets = ticketsQuery.data?.data.tickets ?? [];

  let body: React.ReactNode;
  if (
    ticketsQuery.isLoading ||
    settingsQuery.isLoading ||
    (savedTag !== null && ticketsLabel === null && !ticketsQuery.error)
  ) {
    body = (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  } else if (ticketsQuery.error) {
    body = <Banner variant="error">{ticketsQuery.error.message}</Banner>;
  } else if (savedTag === null) {
    body = (
      <EmptyState
        icon={<Ticket size={48} />}
        title="No tag set"
        description="Set a tag in the setup above. Tickets with that tag will show up here."
      />
    );
  } else if (tickets.length === 0) {
    body = (
      <EmptyState
        icon={<Ticket size={48} />}
        title="No tagged tickets"
        description={`No tickets have the "${savedTag}" tag yet.`}
      />
    );
  } else {
    body = <DataTable columns={columns} data={tickets} aria-label="Tagged tickets table" />;
  }

  return (
    <>
      {settingsQuery.error && <Banner variant="error">{settingsQuery.error.message}</Banner>}
      {settingsQuery.data &&
        (settingsQuery.data.data.tagTriggerLabel ? (
          <TicketConfigSummary
            key={JSON.stringify(settingsQuery.data.data)}
            settings={settingsQuery.data.data}
          />
        ) : (
          <Card padding="lg">
            <CardHeader>
              <Heading level={3}>Set up tag-triggered tests</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                When a ticket gets the tag, an agent tests it and writes the results back to the
                ticket.
              </Text>
            </CardHeader>
            <CardContent>
              <TicketConfigPanel settings={settingsQuery.data.data} />
            </CardContent>
          </Card>
        ))}
      <FilingCard provider={provider} />
      {body}
      {ticketsQuery.data && tickets.length >= ticketsQuery.data.data.limit && (
        <Text size="sm" className="text-muted-foreground">
          Showing the {ticketsQuery.data.data.limit} most recently updated tickets.
        </Text>
      )}
    </>
  );
}

/** The cards for a default source other than Jira, from what the provider supports. */
function OtherSourceCards({ provider }: { provider: TicketingProviderSummary }) {
  return (
    <>
      <UnsupportedCard title="Configuration">
        {provider.id === 'azureDevops' ? (
          <>
            Tag-triggered ticket tests for {provider.displayName} are set up in its{' '}
            <Link to="/settings/$section" params={{ section: 'azure-devops' }} className="underline">
              integration settings
            </Link>
            .
          </>
        ) : (
          `Tag-triggered ticket tests are not available for ${provider.displayName} yet.`
        )}
      </UnsupportedCard>
      <FilingCard provider={provider} />
    </>
  );
}

/**
 * Tickets: the org's default ticket source and what it offers. Jira lists the tickets carrying the
 * tag with the latest agent run for each (runs start from Jira, not from this page); GitHub files
 * tickets for issues. The cards show only what the chosen provider supports.
 */
export function Tickets() {
  useBreadcrumbs([{ key: 'tickets', label: 'Tickets' }]);

  const sourceQuery = useDefaultTicketSource();
  const providersQuery = useTicketingProviders();
  const source = sourceQuery.data?.data;
  const providers = providersQuery.data?.data ?? [];
  const defaultId = source?.defaultProviderId ?? null;
  const defaultProvider = providers.find((provider) => provider.id === defaultId) ?? null;

  let content: React.ReactNode;
  if (sourceQuery.isLoading || providersQuery.isLoading) {
    content = (
      <div className="flex items-center justify-center py-12">
        <LoadingOrbit size="lg" />
      </div>
    );
  } else if (sourceQuery.error) {
    content = <Banner variant="error">{sourceQuery.error.message}</Banner>;
  } else if (!source || source.connected.length === 0) {
    content = (
      <EmptyState
        icon={<Ticket size={48} />}
        title="No ticket provider is connected"
        description={
          <>
            Connect a provider in{' '}
            <Link to="/settings/$section" params={{ section: 'jira' }} className="underline">
              integration settings
            </Link>{' '}
            to test tagged tickets and file tickets for issues.
          </>
        }
      />
    );
  } else {
    content = (
      <>
        {source.connected.length > 1 && (
          <TicketSourceSelect source={source} providers={providers} />
        )}
        {defaultId === null ? (
          <EmptyState
            icon={<Ticket size={48} />}
            title="Choose a ticket source"
            description="More than one ticket provider is connected. Choose the one Cofactor files tickets in and configures here."
          />
        ) : defaultProvider ? (
          defaultId === 'jira' ? (
            <JiraTicketTesting provider={defaultProvider} />
          ) : (
            <OtherSourceCards provider={defaultProvider} />
          )
        ) : (
          <Banner variant="error">
            {getTicketingProviderLabel(defaultId, providers)} is not available.
          </Banner>
        )}
      </>
    );
  }

  return (
    <Page>
      <PageHeader
        icon={<Ticket size={24} />}
        title="Tickets"
        description="Choose where Cofactor files tickets and which tickets it tests."
      />
      <Flex direction="column" gap="lg" className="mt-6">
        {content}
      </Flex>
    </Page>
  );
}
